<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">{{ campaign?.CampaignName || 'Campaign Details' }}</h1>
        <p class="text-gray-600">View campaign details, statistics, and email list information</p>
      </div>
      <div class="flex items-center space-x-3">
        <button
          @click="$router.push('/campaigns')"
          class="btn-secondary"
        >
          ← Back to Campaigns
        </button>
        <button
          v-if="campaign?.Status === 'Sending'"
          @click="pauseCampaign"
          class="btn-secondary flex items-center"
        >
          <PauseIcon class="w-4 h-4 mr-2" />
          Pause Campaign
        </button>
        <button
          v-if="campaign?.Status === 'Paused'"
          @click="resumeCampaign"
          class="btn-primary flex items-center"
        >
          <PlayIcon class="w-4 h-4 mr-2" />
          Resume Campaign
        </button>
        <button
          @click="editCampaign"
          class="btn-primary"
        >
          Edit Campaign
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>

    <div v-else-if="!campaign" class="text-center py-12">
      <EnvelopeIcon class="mx-auto h-12 w-12 text-gray-400" />
      <h3 class="mt-2 text-sm font-medium text-gray-900">Campaign not found</h3>
      <p class="mt-1 text-sm text-gray-500">The campaign you're looking for doesn't exist or has been deleted.</p>
    </div>

    <div v-else class="space-y-6">
      <!-- Campaign Overview -->
      <div class="bg-white rounded-lg shadow-sm p-6">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <h3 class="text-sm font-medium text-gray-500">Status</h3>
            <div class="mt-1 flex items-center">
              <div
                class="w-3 h-3 rounded-full mr-2"
                :class="getCampaignStatusColor(campaign.Status)"
              ></div>
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium"
                :class="getCampaignStatusBadgeClass(campaign.Status)"
              >
                {{ campaign.Status }}
              </span>
            </div>
          </div>
          
          <div>
            <h3 class="text-sm font-medium text-gray-500">Subject</h3>
            <p class="mt-1 text-sm text-gray-900">{{ campaign.Subject }}</p>
          </div>
          
          <div>
            <h3 class="text-sm font-medium text-gray-500">Domain</h3>
            <p class="mt-1 text-sm text-gray-900">{{ campaign.DomainName }}</p>
            <p class="text-xs text-gray-500">Score: {{ campaign.CalculatedScore }}/100</p>
          </div>
          
          <div>
            <h3 class="text-sm font-medium text-gray-500">Scheduled Start</h3>
            <p class="mt-1 text-sm text-gray-900">{{ formatDateTime(campaign.ScheduledStart) }}</p>
          </div>
        </div>
      </div>

      <!-- Statistics -->
      <div class="bg-white rounded-lg shadow-sm">
        <div class="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 class="text-lg font-semibold text-gray-900">Campaign Statistics</h2>
          <button
            @click="refreshStats"
            :disabled="refreshingStats"
            class="btn-secondary text-sm"
          >
            <span v-if="refreshingStats" class="flex items-center">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
              Refreshing...
            </span>
            <span v-else class="flex items-center">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh from Mailgun
            </span>
          </button>
        </div>
        
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
            <button
              @click="setStatusFilter('Sent')"
              :class="statusFilter === 'Sent' ? 'ring-2 ring-primary-600' : ''"
              class="text-center p-4 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div class="text-2xl font-bold text-primary-600">{{ campaign.TotalEmailsSent }}</div>
              <div class="text-sm text-gray-500">Total Sent</div>
            </button>
            
            <button
              @click="setStatusFilter('Opened')"
              :class="statusFilter === 'Opened' ? 'ring-2 ring-success-600' : ''"
              class="text-center p-4 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div class="text-2xl font-bold text-success-600">{{ campaign.TotalEmailsOpened }}</div>
              <div class="text-sm text-gray-500">Opened</div>
            </button>
            
            <button
              @click="setStatusFilter('Clicked')"
              :class="statusFilter === 'Clicked' ? 'ring-2 ring-warning-600' : ''"
              class="text-center p-4 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div class="text-2xl font-bold text-warning-600">{{ campaign.TotalEmailsClicked }}</div>
              <div class="text-sm text-gray-500">Clicked</div>
            </button>
            
            <button
              @click="setStatusFilter('Bounced')"
              :class="statusFilter === 'Bounced' ? 'ring-2 ring-red-600' : ''"
              class="text-center p-4 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div class="text-2xl font-bold text-red-600">{{ campaign.TotalEmailsBounced }}</div>
              <div class="text-sm text-gray-500">Bounced</div>
            </button>
          </div>
          
          <!-- Progress Bar -->
          <div class="mt-6">
            <div class="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Campaign Progress</span>
              <span>{{ campaign.TotalEmailsSent }} / {{ totalSubscribers }}</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div
                class="bg-primary-600 h-2 rounded-full transition-all duration-300"
                :style="{ width: `${progressPercentage}%` }"
              ></div>
            </div>
            <div class="mt-2 text-xs text-gray-500">
              {{ progressPercentage.toFixed(1) }}% complete
            </div>
          </div>
        </div>
      </div>

      <!-- Email Events -->
      <div id="email-events" class="bg-white rounded-lg shadow-sm">
        <div class="p-6 border-b border-gray-200">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-gray-900">Email Events</h2>
            <input
              v-model="emailSearch"
              type="text"
              placeholder="Search emails..."
              class="input-field w-64"
            />
          </div>
          
          <!-- Status Filter Buttons -->
          <div class="flex items-center space-x-4">
            <button
              @click="statusFilter = null"
              :class="statusFilter === null ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              All ({{ allEvents.length }})
            </button>
            <button
              @click="statusFilter = 'Sent'"
              :class="statusFilter === 'Sent' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Sent ({{ campaign.TotalEmailsSent }})
            </button>
            <button
              @click="statusFilter = 'Opened'"
              :class="statusFilter === 'Opened' ? 'bg-success-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Opened ({{ campaign.TotalEmailsOpened }})
            </button>
            <button
              @click="statusFilter = 'Clicked'"
              :class="statusFilter === 'Clicked' ? 'bg-warning-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Clicked ({{ campaign.TotalEmailsClicked }})
            </button>
            <button
              @click="statusFilter = 'Bounced'"
              :class="statusFilter === 'Bounced' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
              class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Bounced ({{ campaign.TotalEmailsBounced }})
            </button>
          </div>
        </div>
        
        <div class="p-6">
          <div v-if="loadingEvents" class="flex justify-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
          
          <div v-else-if="filteredEvents.length === 0" class="text-center py-8 text-gray-500">
            No email events found
          </div>
          
          <div v-else class="space-y-2 max-h-96 overflow-y-auto">
            <div
              v-for="(event, index) in filteredEvents"
              :key="`${event.SendID}-${event.EventType}-${index}`"
              class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div class="flex items-center space-x-3 flex-1">
                <div
                  class="w-2 h-2 rounded-full"
                  :class="getEventStatusColor(event.Status)"
                ></div>
                <div class="flex-1">
                  <div class="text-sm font-medium text-gray-900">{{ event.Email }}</div>
                  <div class="flex items-center space-x-4 mt-1 text-xs">
                    <span :class="event.EventType === 'Sent' ? 'text-gray-600' : event.EventType === 'Opened' ? 'text-green-600' : event.EventType === 'Clicked' ? 'text-warning-600' : 'text-red-600'">
                      <span class="font-medium">{{ event.EventType }}:</span>
                      <span class="ml-1">{{ formatDateTime(event.Timestamp) }}</span>
                    </span>
                  </div>
                  <div v-if="event.BounceReason" class="mt-1 text-xs text-red-600">
                    {{ event.BounceReason }}
                  </div>
                </div>
              </div>
              <div>
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="getEventStatusBadgeClass(event.Status)"
                >
                  {{ event.Status }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Email Lists -->
      <div class="bg-white rounded-lg shadow-sm">
        <div class="p-6 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold text-gray-900">Email Lists</h2>
            <div class="flex items-center space-x-3">
              <input
                v-model="listSearch"
                type="text"
                placeholder="Search lists..."
                class="input-field w-64"
              />
            </div>
          </div>
        </div>
        
        <div class="p-6">
          <div v-if="filteredEmailLists.length === 0" class="text-center py-8">
            <ListBulletIcon class="mx-auto h-12 w-12 text-gray-400" />
            <h3 class="mt-2 text-sm font-medium text-gray-900">No email lists found</h3>
            <p class="mt-1 text-sm text-gray-500">This campaign is not associated with any email lists.</p>
          </div>
          
          <div v-else class="space-y-4">
            <div
              v-for="list in filteredEmailLists"
              :key="list.ListID"
              class="border border-gray-200 rounded-lg p-4"
            >
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <h3 class="font-medium text-gray-900">{{ list.ListName }}</h3>
                  <p v-if="list.Description" class="text-sm text-gray-500 mt-1">
                    {{ list.Description }}
                  </p>
                  <div class="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                    <span>{{ list.TotalEmails }} subscribers</span>
                    <span v-if="list.ClubName" class="text-primary-600">
                      Club: {{ list.ClubName }}
                    </span>
                  </div>
                </div>
                
                <div class="flex items-center space-x-2">
                  <button
                    @click="viewListDetails(list)"
                    class="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Campaign Timeline -->
      <div class="bg-white rounded-lg shadow-sm">
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900">Campaign Timeline</h2>
        </div>
        
        <div class="p-6">
          <div class="space-y-4">
            <div class="flex items-center space-x-4">
              <div class="w-3 h-3 bg-primary-600 rounded-full"></div>
              <div>
                <div class="text-sm font-medium text-gray-900">Campaign Created</div>
                <div class="text-sm text-gray-500">{{ formatDateTime(campaign.CreatedAt) }}</div>
              </div>
            </div>
            
            <div v-if="campaign.ScheduledStart" class="flex items-center space-x-4">
              <div class="w-3 h-3 bg-warning-500 rounded-full"></div>
              <div>
                <div class="text-sm font-medium text-gray-900">Scheduled to Start</div>
                <div class="text-sm text-gray-500">{{ formatDateTime(campaign.ScheduledStart) }}</div>
              </div>
            </div>
            
            <div v-if="campaign.ActualStart" class="flex items-center space-x-4">
              <div class="w-3 h-3 bg-success-500 rounded-full"></div>
              <div>
                <div class="text-sm font-medium text-gray-900">Campaign Started</div>
                <div class="text-sm text-gray-500">{{ formatDateTime(campaign.ActualStart) }}</div>
              </div>
            </div>
            
            <div v-if="campaign.ActualEnd" class="flex items-center space-x-4">
              <div class="w-3 h-3 bg-gray-500 rounded-full"></div>
              <div>
                <div class="text-sm font-medium text-gray-900">Campaign Completed</div>
                <div class="text-sm text-gray-500">{{ formatDateTime(campaign.ActualEnd) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { campaignsApi } from '../services/api'
import { toast } from 'vue3-toastify'
import {
  EnvelopeIcon,
  ListBulletIcon,
  PauseIcon,
  PlayIcon
} from '@heroicons/vue/24/outline'

const route = useRoute()
const router = useRouter()

// State
const campaign = ref(null)
const loading = ref(false)
const listSearch = ref('')
const refreshingStats = ref(false)
const emailEvents = ref([])
const loadingEvents = ref(false)
const emailSearch = ref('')
const statusFilter = ref(null)

// Computed
const filteredEmailLists = computed(() => {
  if (!campaign.value?.emailLists) return []
  
  if (!listSearch.value) return campaign.value.emailLists
  
  const search = listSearch.value.toLowerCase()
  return campaign.value.emailLists.filter(list => 
    list.ListName.toLowerCase().includes(search) ||
    (list.Description && list.Description.toLowerCase().includes(search))
  )
})

const totalSubscribers = computed(() => {
  if (!campaign.value?.emailLists) return 0
  return campaign.value.emailLists.reduce((total, list) => total + list.TotalEmails, 0)
})

const progressPercentage = computed(() => {
  if (totalSubscribers.value === 0) return 0
  return (campaign.value.TotalEmailsSent / totalSubscribers.value) * 100
})

// Create event items from sends (one for sent, one for opened, etc.)
const allEvents = computed(() => {
  const events = []
  
  emailEvents.value.forEach(send => {
    // Always add a "Sent" event if SentAt exists
    if (send.SentAt) {
      events.push({
        SendID: send.SendID,
        Email: send.Email,
        Status: 'Sent',
        EventType: 'Sent',
        Timestamp: send.SentAt,
        BounceReason: null
      })
    }
    
    // Add "Opened" event if OpenedAt exists
    if (send.OpenedAt) {
      events.push({
        SendID: send.SendID,
        Email: send.Email,
        Status: 'Opened',
        EventType: 'Opened',
        Timestamp: send.OpenedAt,
        BounceReason: null
      })
    }
    
    // Add "Clicked" event if ClickedAt exists
    if (send.ClickedAt) {
      events.push({
        SendID: send.SendID,
        Email: send.Email,
        Status: 'Clicked',
        EventType: 'Clicked',
        Timestamp: send.ClickedAt,
        BounceReason: null
      })
    }
    
    // Add "Bounced" event if BouncedAt exists
    if (send.BouncedAt) {
      events.push({
        SendID: send.SendID,
        Email: send.Email,
        Status: 'Bounced',
        EventType: 'Bounced',
        Timestamp: send.BouncedAt,
        BounceReason: send.BounceReason
      })
    }
  })
  
  // Sort by timestamp descending (most recent first)
  return events.sort((a, b) => new Date(b.Timestamp) - new Date(a.Timestamp))
})

const filteredEvents = computed(() => {
  let filtered = allEvents.value
  
  // Filter by status
  if (statusFilter.value) {
    filtered = filtered.filter(e => e.Status === statusFilter.value)
  }
  
  // Filter by search
  if (emailSearch.value) {
    const search = emailSearch.value.toLowerCase()
    filtered = filtered.filter(e => e.Email.toLowerCase().includes(search))
  }
  
  return filtered
})

// Methods
const getCampaignStatusColor = (status) => {
  switch (status) {
    case 'Scheduled':
      return 'bg-warning-500'
    case 'Sending':
      return 'bg-success-500'
    case 'Completed':
      return 'bg-gray-400'
    case 'Paused':
      return 'bg-gray-300'
    default:
      return 'bg-gray-400'
  }
}

const getCampaignStatusBadgeClass = (status) => {
  switch (status) {
    case 'Scheduled':
      return 'bg-warning-100 text-warning-800'
    case 'Sending':
      return 'bg-success-100 text-success-800'
    case 'Completed':
      return 'bg-gray-100 text-gray-800'
    case 'Paused':
      return 'bg-gray-100 text-gray-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const getEventStatusColor = (status) => {
  switch (status) {
    case 'Sent':
      return 'bg-primary-600'
    case 'Opened':
      return 'bg-success-600'
    case 'Clicked':
      return 'bg-warning-600'
    case 'Bounced':
      return 'bg-red-600'
    default:
      return 'bg-gray-400'
  }
}

const getEventStatusBadgeClass = (status) => {
  switch (status) {
    case 'Sent':
      return 'bg-primary-100 text-primary-800'
    case 'Opened':
      return 'bg-success-100 text-success-800'
    case 'Clicked':
      return 'bg-warning-100 text-warning-800'
    case 'Bounced':
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const formatDate = (dateString) => {
  if (!dateString) return 'Not scheduled'
  return new Date(dateString).toLocaleDateString()
}

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleString()
}

const fetchCampaign = async () => {
  try {
    loading.value = true
    const response = await campaignsApi.getById(route.params.id)
    if (response.data.success) {
      campaign.value = response.data.data
    }
  } catch (error) {
    console.error('Error fetching campaign:', error)
    toast.error('Failed to fetch campaign details')
  } finally {
    loading.value = false
  }
}

const fetchEmailEvents = async () => {
  if (!route.params.id) return
  
  try {
    loadingEvents.value = true
    const response = await campaignsApi.getSends(route.params.id)
    if (response.data.success) {
      emailEvents.value = response.data.data || []
    }
  } catch (error) {
    console.error('Error fetching email events:', error)
    toast.error('Failed to fetch email events')
  } finally {
    loadingEvents.value = false
  }
}

const pauseCampaign = async () => {
  if (!campaign.value) return
  
  try {
    await campaignsApi.pause(campaign.value.CampaignID)
    toast.success('Campaign paused successfully')
    await fetchCampaign()
  } catch (error) {
    console.error('Error pausing campaign:', error)
    toast.error(error.response?.data?.error || 'Failed to pause campaign')
  }
}

const resumeCampaign = async () => {
  if (!campaign.value) return
  
  try {
    await campaignsApi.resume(campaign.value.CampaignID)
    toast.success('Campaign resumed successfully')
    await fetchCampaign()
  } catch (error) {
    console.error('Error resuming campaign:', error)
    toast.error(error.response?.data?.error || 'Failed to resume campaign')
  }
}

const editCampaign = () => {
  router.push(`/campaigns?edit=${campaign.value.CampaignID}`)
}

const viewListDetails = (list) => {
  router.push(`/lists/${list.ListID}`)
}

const setStatusFilter = (status) => {
  statusFilter.value = statusFilter.value === status ? null : status
  // Scroll to email events section
  setTimeout(() => {
    const element = document.getElementById('email-events')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, 100)
}

const refreshStats = async () => {
  if (!campaign.value) return
  
  try {
    refreshingStats.value = true
    const response = await campaignsApi.refreshStats(route.params.id)
    
    if (response.data.success) {
      toast.success(`Statistics refreshed: ${response.data.data.updated} emails updated`)
      // Refresh campaign data and events to show updated statistics
      await Promise.all([
        fetchCampaign(),
        fetchEmailEvents()
      ])
    }
  } catch (error) {
    console.error('Error refreshing stats:', error)
    // Error toast is handled by API interceptor
  } finally {
    refreshingStats.value = false
  }
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchCampaign(),
    fetchEmailEvents()
  ])
})
</script> 